import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

interface WorkFlowFilterCallbackParams {
  sample_use?: boolean;
  group_name?: string;
  library_name?: string | null;
  workflow_name?: string;
  round?: number;
  [key: string]: any;
}

export interface iDrugToxicityUserParameterState {
  smiles: string | null;
  compound_name: string | null;
  response: any;
  isFilterCallbackLoading: boolean;
  isLoadedFromTemporarySave: boolean;
  isSampleData: boolean;
  sample_use: boolean;
  message: string | null;
  error: string | null;
}

const initialState: iDrugToxicityUserParameterState = {
  smiles: null,
  compound_name: null,
  response: null,
  isFilterCallbackLoading: false,
  isLoadedFromTemporarySave: false,
  isSampleData: false,
  sample_use: false,
  message: "",
  error: null,
};

const applyCallbackResponse = (
  state: iDrugToxicityUserParameterState,
  response: any
) => {
  state.response = response;

  const getFirstValue = (...candidates: any[]) => {
    for (const candidate of candidates) {
      if (candidate !== undefined && candidate !== null && candidate !== "") {
        return candidate;
      }
    }

    return undefined;
  };

  const callbackPayload =
    response?.filter ||
    response?.filters ||
    response?.parameter ||
    response?.parameters ||
    response?.input ||
    response?.inputs ||
    response?.data ||
    response;

  if (response?.sample_use !== undefined) {
    state.sample_use = response.sample_use;
    state.isSampleData = response.sample_use;
  }

  const smiles = getFirstValue(
    response?.smiles,
    response?.drug_smiles,
    response?.compound_smiles,
    callbackPayload?.smiles,
    callbackPayload?.drug_smiles,
    callbackPayload?.compound_smiles
  );

  const compoundName = getFirstValue(
    response?.compound_name,
    response?.compoundName,
    response?.name,
    response?.drug_name,
    callbackPayload?.compound_name,
    callbackPayload?.compoundName,
    callbackPayload?.name,
    callbackPayload?.drug_name
  );

  if (smiles !== undefined) {
    state.smiles = smiles;
  }

  if (compoundName !== undefined) {
    state.compound_name = compoundName;
  }
};

export const temporarySaveFilterCallbackAPI = createAsyncThunk(
  "userDrugToxicityParameter/temporarySaveFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/temp-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        Object.keys(params).length > 0 ? params : {},
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const workFlowFilterCallbackAPI = createAsyncThunk(
  "userDrugToxicityParameter/workFlowFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/filter-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        Object.keys(params).length > 0 ? params : {},
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const userDrugToxicityParameterSlice = createSlice({
  name: "userDrugToxicityParameter",
  initialState,
  reducers: {
    setToxicitySmiles: (state, action: PayloadAction<string | null>) => {
      state.smiles = action.payload;
    },
    setToxicityCompoundName: (state, action: PayloadAction<string | null>) => {
      state.compound_name = action.payload;
    },
    resetAllParameters: (state) => {
      Object.assign(state, initialState);
    },
    setIsLoadedFromTemporarySave: (state, action: PayloadAction<boolean>) => {
      state.isLoadedFromTemporarySave = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(temporarySaveFilterCallbackAPI.pending, (state) => {
        state.isFilterCallbackLoading = true;
        state.isLoadedFromTemporarySave = false;
        state.error = null;
      })
      .addCase(temporarySaveFilterCallbackAPI.fulfilled, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.isLoadedFromTemporarySave = true;
        state.error = null;
        applyCallbackResponse(state, action.payload);
      })
      .addCase(temporarySaveFilterCallbackAPI.rejected, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.error = action.payload as string;
      })
      .addCase(workFlowFilterCallbackAPI.pending, (state) => {
        state.isFilterCallbackLoading = true;
        state.isLoadedFromTemporarySave = false;
        state.error = null;
      })
      .addCase(workFlowFilterCallbackAPI.fulfilled, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.isLoadedFromTemporarySave = false;
        state.error = null;
        applyCallbackResponse(state, action.payload);
      })
      .addCase(workFlowFilterCallbackAPI.rejected, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setToxicitySmiles,
  setToxicityCompoundName,
  resetAllParameters,
  setIsLoadedFromTemporarySave,
} = userDrugToxicityParameterSlice.actions;

export default userDrugToxicityParameterSlice.reducer;
